"use client";

import { Suspense, useState } from "react";
import CleanModeWatcher from "./cleanModeWatcher.component";

interface LayoutShellProps {
  header: React.ReactNode;
  fab: React.ReactNode;
  footer: React.ReactNode;
  children: React.ReactNode;
}

// Header/footer/fab are rendered by the (server) root layout and passed in
// as already-rendered nodes rather than imported here - this file only
// needs to exist as a client boundary for the "clean mode" (?clean=true)
// check, so nothing else gets forced into the client bundle through it.
//
// useSearchParams() used to be called directly in this component's own
// body, with header/children/fab/footer returned from that same function -
// since useSearchParams() can't resolve at build time for a statically
// rendered page, that forced this ENTIRE component's output (i.e. every
// page's whole visible content: header, hero, images, everything) to bail
// out of static rendering into a client-only render
// (`BAILOUT_TO_CLIENT_SIDE_RENDERING`), confirmed by inspecting the raw
// server HTML: zero real <img>/<h1>/<button> tags anywhere in the response,
// just an empty <div> shell - the entire page only became visible once
// hydration finished downloading and running every chunk needed for the
// whole tree, matching the ~3-4s+ delay behind every LCP measurement this
// session regardless of what else was optimized. Isolating the
// useSearchParams() read into its own null-rendering child, inside its own
// (separately, harmlessly deferred) Suspense boundary, keeps
// header/children/fab/footer outside of anything that needs to suspend, so
// they render as real static HTML again.
export default function LayoutShell({
  header,
  fab,
  footer,
  children,
}: LayoutShellProps) {
  const [isClean, setIsClean] = useState(false);

  return (
    <>
      <Suspense fallback={null}>
        <CleanModeWatcher onChange={setIsClean} />
      </Suspense>
      {!isClean && header}
      {children}
      {!isClean && fab}
      {!isClean && footer}
    </>
  );
}
